import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { useEffect, useState } from "react";
import moment from "moment";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3000";

const StoryManagement = ({ provider }) => {
  const [loading, setLoading] = useState(false);
  const [stories, setStories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaType, setMediaType] = useState("image");
  const [preview, setPreview] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [viewStory, setViewStory] = useState(null);

  // ✅ Fetch provider stories
  const fetchStories = async () => {
    console.log(provider);
    
    if (!provider?._id) return;
    try {
      setLoading(true);
      
      const res = await axios.get(
        `${API_BASE}/api/stories/getProviderStories/${provider._id}`
      );
      setStories(res.data.stories || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log(provider,"providerproviderprovider");
    
    fetchStories();
  }, [provider]);

  // ✅ Upload new story
  const handleUpload = async () => {
    if (!selectedFile) return alert("Please select a file!");
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("providerId", provider._id);
    formData.append("mediaType", mediaType);

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/stories/upload/story`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOpenDialog(false);
      setSelectedFile(null);
      setPreview(null);
      fetchStories();
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete story
  const handleDelete = async (storyId) => {
    if (!window.confirm("Delete this story?")) return;
    try {
      await axios.delete(`${API_BASE}/api/stories/deleteStory/${storyId}`);
      fetchStories();
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete story");
    }
  };

  return (
    <Box sx={{ maxWidth: "99%", mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Story Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Story
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : stories.length === 0 ? (
        <Typography>No stories found</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Media</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Views</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stories.map((s) => (
                <TableRow key={s.storyId}>
                  <TableCell>
                    {s.mediaType === "video" ? (
                      <VideoIcon color="primary" />
                    ) : (
                      <ImageIcon color="secondary" />
                    )}
                  </TableCell>
                  <TableCell>{s.mediaType}</TableCell>
                  <TableCell>{s.views}</TableCell>
                  <TableCell>
                    {moment(s.createdAt).format("DD-MM-YYYY HH:mm")}
                  </TableCell>
                  <TableCell>{s.providerName}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => {
                        setViewStory(s);
                        setOpenView(true);
                      }}
                      color="info"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(s.storyId)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload New Story</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <FormControl fullWidth>
            <InputLabel id="media-type-label">Media Type</InputLabel>
            <Select
              labelId="media-type-label"
              value={mediaType}
              label="Media Type"
              onChange={(e) => setMediaType(e.target.value)}
            >
              <MenuItem value="image">Image</MenuItem>
              <MenuItem value="video">Video</MenuItem>
            </Select>
          </FormControl>

          <TextField
            type="file"
            inputProps={{
              accept:
                mediaType === "image" ? "image/*" : "video/*",
            }}
            onChange={(e) => {
              const file = e.target.files[0];
              setSelectedFile(file);
              if (file && mediaType === "image") {
                setPreview(URL.createObjectURL(file));
              } else {
                setPreview(null);
              }
            }}
          />

          {preview && (
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: 250,
                  objectFit: "cover",
                  borderRadius: 10,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Story Preview</DialogTitle>
        <DialogContent sx={{ textAlign: "center", mt: 1 }}>
          {viewStory &&
            (viewStory.mediaType === "video" ? (
              <video
                src={viewStory.mediaUrl}
                controls
                style={{ width: "100%", borderRadius: 10 }}
              />
            ) : (
              <img
                src={viewStory.mediaUrl}
                alt="story"
                style={{
                  width: "100%",
                  borderRadius: 10,
                  objectFit: "contain",
                }}
              />
            ))}
          {viewStory && (
            <Typography sx={{ mt: 2 }}>
              Uploaded: {moment(viewStory.createdAt).format("DD-MM-YYYY HH:mm")}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StoryManagement;
